import React from 'react';
import { call, delay, put, race, select, take, takeEvery, takeLeading } from 'redux-saga/effects';

import { a2aTransfersActions } from 'app-components/flux/a2aTransfers';
import { accountsActions } from 'app-components/flux/accounts';
import { achievementsActions } from 'app-components/flux/achievements';
import { alertsActions } from 'app-components/flux/alerts';
import { authSelectors } from 'app-components/flux/auth';
import { billPresentmentActions, billPresentmentUtils } from 'app-components/flux/bill-presentment';
import { reportConfigurationsActions } from 'app-components/flux/report-configurations';
import { billableInvoiceItemsActions } from 'app-components/flux/billable-invoice-items/billableInvoiceItemsSlice';
import { businessActions } from 'app-components/flux/business';
import { categoriesActions } from 'app-components/flux/categories';
import { resourceSagaUtils } from 'app-components/flux/core';
import { coreActions } from 'app-components/flux/core-actions';
import { getCreditProfile } from 'app-components/flux/credit-profile/actions';
import { customersActions } from 'app-components/flux/customers';
import { datasetsActions } from 'app-components/flux/datasets';
import { documentsActions } from 'app-components/flux/documents';
import { entitlementsActions } from 'app-components/flux/entitlements';
import { featureFlagsActions, featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { filtersActions } from 'app-components/flux/filters';
import { freeToSpendActions } from 'app-components/flux/free-to-spend';
import { goalsActions } from 'app-components/flux/goals';
import { institutionLoginsActions, institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { investmentHoldingsV2Actions } from 'app-components/flux/investment-holdings-v2';
import { invoiceItemsActions } from 'app-components/flux/invoice-items';
import { jobStatusActions } from 'app-components/flux/job-status';
import { mileageTrackerActions } from 'app-components/flux/mileage-tracker/mileageTrackerSlice';
import { milestonesActions } from 'app-components/flux/milestones';
import { npsActions } from 'app-components/flux/nps';
import { preferencesActions } from 'app-components/flux/preferences';
import { preferencesV2Actions, preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { profileActions } from 'app-components/flux/profile';
import { projectsActions } from 'app-components/flux/projects';
import { salesTaxesActions } from 'app-components/flux/sales-taxes/salesTaxesSlice';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
} from 'app-components/flux/scheduled-transactions';
import { securitiesActions } from 'app-components/flux/securities';
import { spendingWatchlistActions } from 'app-components/flux/spending-watchlist';
import { subscriptionsActions } from 'app-components/flux/subscriptions';
import { tagsActions } from 'app-components/flux/tags';
import { transactionsActions } from 'app-components/flux/transactions';
import { userInfoActions } from 'app-components/flux/user-info';
import { fetchOnce } from 'app-components/hooks/utils';
// todo: address history import
import { getEnvironmentConfig, getLogger, history, localPreferences } from 'app-components/utils/core';

import { setAwaitingToAutoRefresh } from 'data/app/actions';
import { initializeFeatureFlags } from 'utils/featureFlags/initialize';

import { getFundingAccounts } from 'data/fundingAccounts/actions';
// import { getTransferHistory } from 'data/transferHistory/transferHistoryActions';

import { navigate } from 'NavigateSetter';
import AxiosFactory from 'utils/axiosFactory';

import Link from '@mui/material/Link';
import dispatchConfirmationDialog from 'components/Dialogs/ConfirmationDialog/dispatch';
import * as actions from './actions';

const qcsAxios = AxiosFactory.get('qcs');
const log = getLogger('data/app/sagas.js');

// TODO: do we still need this - perhaps remove?
function* appHistoryPush(action) {
  const {
    payload: { location },
  } = action;

  yield put(history.push(location));
}

function* appHistoryReplace(action) {
  const {
    payload: { location },
  } = action;

  yield put(history.replace(location));
}

function* fetchUserLevelData() {
  log.log('fetch data (user level)...');

  yield put(subscriptionsActions.getSubscriptions());
  yield put(entitlementsActions.getEntitlements());
  yield put(datasetsActions.getDatasets());
  yield put(preferencesActions.getPreferencesUser());
  yield put(userInfoActions.getUserInfo());
}

function* fetchData() {
  log.log('fetch data (common)...');

  yield put(preferencesActions.getPreferencesDataset());
  yield put(institutionLoginsActions.getLoginsWithFiIssues());
  yield put(accountsActions.getAccounts());
  yield put(tagsActions.getTags());
  yield put(categoriesActions.getCategories());
  yield put(documentsActions.getDocuments());
}

function* fetchFeaturedData() {
  log.log('fetch data (featured)...');

  // investment transactions filtered depending on 'investmentTransactions' FF
  yield put(transactionsActions.getTransactions());
  yield put(transactionsActions.getTransactionsEarliestDateOn());

  const featureFlags = yield select(featureFlagsSelectors.getFeatureFlags);

  if (featureFlags.get('nps')) {
    yield put(npsActions.checkIfNPSSurveyRequestedAction());
  }
  if (featureFlags.get('scheduledTransactions') || featureFlags.get('scheduledTransactionsInRegister')) {
    yield put(scheduledTransactionsActions.getScheduledTransactions());
  }

  yield put(reportConfigurationsActions.getReportConfigurations());
  yield put(freeToSpendActions.getFreeToSpend());

  // spending watchlist
  yield put(filtersActions.getFilters());
  yield put(spendingWatchlistActions.getSpendingWatchListsInitial());
  if (featureFlags.get('inProductNotifications')) {
    yield put(alertsActions.getAlerts());
  }
  yield put(billPresentmentActions.fetch());
  // goals
  yield put(goalsActions.getGoals());
  // achievements
  yield put(achievementsActions.getAchievements());
  yield put(milestonesActions.getMilestones());
  // investments
  yield put(investmentHoldingsV2Actions.getInvestmentHoldingsV2());
  if (featureFlags.get('securities')) {
    yield put(securitiesActions.getSecurities());
  }
  if (featureFlags.get('businessEnabled')) {
    yield put(businessActions.getBusiness());
    yield put(customersActions.getCustomers());
    yield put(projectsActions.getProject());
    yield put(invoiceItemsActions.fetch());
    yield put(billableInvoiceItemsActions.fetch());
    yield put(salesTaxesActions.fetch());
    yield put(mileageTrackerActions.fetch());
  }
  // credit profile
  const dataset = yield select(authSelectors.getCurrentDataset);
  if (featureFlags.get('creditReport') && !dataset?.shared) {
    yield put(getCreditProfile());
  }

  const isSharedDataset = yield select((state) => authSelectors.isCurrentDatasetShared(state));

  yield put(profileActions.getProfile());
  yield put(profileActions.getVerificationStatus());
  if (!isSharedDataset) {
    log.log('a2aTransfers Allowed! - Fetching');
    yield put(a2aTransfersActions.getA2aTransfers());
    yield put(getFundingAccounts());
  }
}

function* startRefreshAccounts(payload) {
  log.log('accounts refresh triggered...');

  yield put(institutionLoginsActions.refreshAccounts(payload));

  const datasetId = yield select(authSelectors.getDatasetId);
  const now = Date.now();
  localPreferences.setItem(`${datasetId}_last_auto_refresh_time`, now.toString());
}

// TODO: switch to web socket - and remove method below
function* fetchRefreshedData(action) {
  log.log('fetch data (refreshed)...', action);

  if (lastContext === 'refreshAll' && action.payload.status === 'DONE_PROCESSING_WITHOUT_UPDATE') {
    const institutionLogins = yield select(institutionLoginsSelectors.getActiveConnectedInstitutionLogins);
    const institutionLoginsSnoozed = institutionLogins.filter(
      (institutionLogin) => institutionLogin.refreshFrequency !== 'NONE',
    );
    if (institutionLogins?.size && institutionLogins?.size === institutionLoginsSnoozed?.size) {
      dispatchConfirmationDialog({
        size: 'xs',
        title: 'Warning',
        content: (
          <div>
            All of your accounts have update schedules that are not yet due to refresh. To refresh them now, or update
            their refresh schedule, go to <Link href='/settings/accounts'>Settings</Link>.
          </div>
        ),
        confirmLabel: 'Settings',
        confirmButtonVariant: 'contained',
        denyLabel: 'Cancel',
        denyButtonVariant: 'outlined',
        confirmAction: () => {
          navigate('/settings/accounts');
        },
      });
    }
  }

  yield put(institutionLoginsActions.getInstitutionLogins());
  yield put(accountsActions.getAccounts());
  yield put(transactionsActions.getTransactions());
  yield put(transactionsActions.getTransactionsEarliestDateOn());

  const featureFlags = yield select(featureFlagsSelectors.getFeatureFlags);
  // spendingPlan
  yield put(freeToSpendActions.getFreeToSpend(undefined, { context: 'post refresh' }));
  // spendingWatchlist
  yield put(spendingWatchlistActions.getSpendingWatchLists());
  // investments
  yield put(investmentHoldingsV2Actions.getInvestmentHoldingsV2());
  if (featureFlags.get('scheduledTransactions') || featureFlags.get('scheduledTransactionsInRegister')) {
    yield put(scheduledTransactionsActions.getScheduledTransactions());
  }
  if (featureFlags.get('inProductNotifications')) {
    yield put(alertsActions.getAlerts());
  }
  // achievements
  yield put(achievementsActions.getAchievements());
  yield put(milestonesActions.getMilestones());
  if (featureFlags.get('securities')) {
    yield put(securitiesActions.getSecurities());
  }

  // refresh goal balances a few seconds after refresh has completed
  yield delay(3000);
  yield put(goalsActions.refreshGoalsBalances());
}

let lastContext;

function* appRefreshAll(action) {
  lastContext = action.meta?.context;

  let refreshStarted;
  let createJobStatusSuccessAction;

  try {
    log.info('refresh (start)->');

    if (action.meta?.props?.crashSaga) {
      throw new Error('Simulate Saga Crash');
    }

    const authTokenValid = yield select(authSelectors.isAuthTokenValid);
    if (!authTokenValid) {
      log.warn('refresh (invalid auth token) X');
    } else {
      // fetch user level data
      yield call(fetchUserLevelData);

      const datasetId = yield select(authSelectors.getDatasetId);
      if (datasetId) {
        yield put(
          coreActions.setUpdateAllState({
            updateAllFailed: null,
            updateAllMessages: null,
          }),
        );

        // if create job status fails, don't retry just continue loading
        yield put(jobStatusActions.createJobStatus({ type: 'SYNC', datasetId }, { attemptsLeft: 0 }));
        const createJobStatus = yield race({
          createJobStatusSuccessAction: take(jobStatusActions.createJobStatusSuccess),
          createJobStatusFailureAction: take(jobStatusActions.createJobStatusFailure),
        });
        createJobStatusSuccessAction = createJobStatus.createJobStatusSuccessAction;

        yield call(fetchOnce, preferencesV2Actions.getPreferencesV2, preferencesV2Selectors, true, false);

        // fetch data
        yield call(fetchData);

        yield resourceSagaUtils.fetchWait(
          preferencesV2Selectors,
          preferencesV2Actions.getPreferencesV2Success,
          preferencesV2Actions.getPreferencesV2Failure,
        );

        // fetch featured data
        const featureFlagsLoadPending = yield select(featureFlagsSelectors.getLoadPending);
        if (!featureFlagsLoadPending) {
          yield call(fetchFeaturedData);
        } else {
          const featureFlagsIsLoading = yield select(featureFlagsSelectors.getIsLoading);
          if (!featureFlagsIsLoading) {
            yield call(initializeFeatureFlags);
          }
          yield call(function* waitForFlags() {
            yield race([
              take(featureFlagsActions.getFeatureFlagsSuccess),
              take(featureFlagsActions.getFeatureFlagsFailure),
            ]);
            yield call(fetchFeaturedData);
          });
        }

        // trigger fi refresh
        if (action.meta?.context === 'init') {
          const lastRefreshTimeStr = localPreferences.getItem(`${datasetId}_last_auto_refresh_time`);
          const lastRefreshTime = lastRefreshTimeStr ? parseInt(lastRefreshTimeStr, 10) : 0;
          const timeOkToAutoRefresh = lastRefreshTime + 60 * 60 * 1000;
          const now = Date.now();
          if (now >= timeOkToAutoRefresh) {
            // we are delaying just a bit so user doesn't get a Credential Required or MFA dialog immediately upon login
            yield put(setAwaitingToAutoRefresh(true));
            yield delay(5000);
            yield call(startRefreshAccounts, { autoRefresh: true });
            refreshStarted = true;
          }
        } else {
          yield call(startRefreshAccounts);
          refreshStarted = true;
        }
      }
      yield put(datasetsActions.getDatasets()); // another hack to ensure that lastSyncedAt is up to date
    }
  } catch (e) {
    assert(false, e);
  } finally {
    if (refreshStarted) {
      yield take(institutionLoginsActions.refreshAccountsCompleted);
      yield put(setAwaitingToAutoRefresh(false));

      const scheduledTxns = yield select(scheduledTransactionsSelectors.getResourcesById);
      billPresentmentUtils.trackFiServModels(scheduledTxns);
    }

    // even if create job status fails, need to still delete job status (for reducer benefit)
    yield put(jobStatusActions.deleteJobStatus({ id: createJobStatusSuccessAction?.payload?.id })); // it's a hack - refresh is complete, but fetch still is in progress

    log.info('refresh (complete)<-');
  }
}

// =================================================================================================
// upsertPushNotificationToken
// =================================================================================================

function* upsertPushNotificationToken({ payload: token }) {
  log.info('upsertPushNotificationToken called...', token);

  const datasetId = yield select(authSelectors.getDatasetId);

  const requestUrl = `${getEnvironmentConfig().services_url}/messenger/push-token`;
  const requestData = {
    datasetId,
    tokenType: 'FCM',
    pushToken: token,
    devicePlatform: 'web',
  };

  try {
    const response = yield call(
      qcsAxios.post,
      requestUrl,
      requestData,
      datasetId ? undefined : { headers: { datasetId: null } },
    );
    log.info('upsertPushNotificationToken response...', response);

    // yield put(actions.searchForInstitutionsSuccess(institutions));
  } catch (error) {
    log.error('upsertPushNotificationToken error...', error);
    // yield put(actions.searchForInstitutionsFailure(error));
  }
}

// =============================================================================
// Watchers
// =============================================================================

function* appHistoryPushActionWatcher() {
  yield takeEvery(actions.appHistoryPush, appHistoryPush);
}

function* appHistoryReplaceActionWatcher() {
  yield takeEvery(actions.appHistoryReplace, appHistoryReplace);
}

function* appRefreshAllActionWatcher() {
  yield takeLeading(actions.appRefreshAll, appRefreshAll);
}

function* refreshAccountsCompletedActionWatcher() {
  yield takeLeading(institutionLoginsActions.refreshAccountsCompleted, fetchRefreshedData);
}

function* upsertPushNotificationTokenWatcher() {
  yield takeLeading(actions.appSubmitPushNotificationToken, upsertPushNotificationToken);
}

export default [
  appHistoryPushActionWatcher,
  appHistoryReplaceActionWatcher,
  appRefreshAllActionWatcher,
  refreshAccountsCompletedActionWatcher,
  upsertPushNotificationTokenWatcher,
];
